import type { CollectionWithPrice } from "@/types/api";
import { cn, createInCartHandler } from "@/utils/utils";
import React, { useEffect, useState, type JSX } from "react";
import { AlertCircle, Plus, ShoppingCart } from "lucide-react";
import { useCart } from "../contexts/cart-context";
import { useProducts } from "../contexts/product-context";
import { Button } from "@/components/ui/button";
import { MUS00003 } from "@/constants/auth-sku";

export type CollectionVariant = "marketplace" | "in-cart";

export interface CollectionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  collection: CollectionWithPrice;
  inCart?: boolean;
  onClick?: () => void;
}

const renderButtonByVariant: Record<
  CollectionVariant,
  (args: { incart: boolean; onClick: (e: React.MouseEvent) => void, className?: string }) => JSX.Element
> = {
  marketplace: ({ incart, onClick, className }) => (
    <Button
      className={cn(
        "h-8 rounded-full flex items-center justify-center px-3 gap-2",
        "text-sm font-medium text-gray-500",
        "transition-colors duration-300",
        (incart ? "bg-musio-red text-musio-light-lilac hover:bg-musio-red/90"
          : "bg-musio-green text-musio-mid-gray hover:bg-musio-green/90"),
        "cart-button",
        className,
      )}
      onClick={onClick}
      role="button"
      id="cart-button"
      aria-label={incart ? "Remove From Cart" : "Add To Cart"}
    >
      <ShoppingCart
        className={cn(
          "w-5 h-5 transition-transform duration-300",
          incart ? "text-musio-light-lilac" : "text-musio-mid-gray"
        )}
      />
      <span>{incart ? "Remove From Cart" : "Add To Cart"}</span>
    </Button>
  ),

  "in-cart": ({ incart, onClick, className }) => (
    <Button
      className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center",
        "text-xl font-bold text-gray-500",
        "transition-colors duration-300",
        (incart ? "bg-musio-red hover:bg-musio-red/90"
          : "bg-musio-green hover:bg-musio-green/90"),
        "cart-button",
        className,
      )}
      id="cart-button"
      onClick={onClick}
      role="button"
      aria-label={incart ? "Remove From Cart" : "Add To Cart"}
    >
      <Plus
        className={cn(
          "w-5 h-5 transition-transform duration-300",
          incart ? "-rotate-45 text-musio-light-lilac"
            : "rotate-0 text-musio-mid-gray"
        )}
      />
    </Button>
  ),
};

const CollectionGrid: React.FC<CollectionProps> = ({
  collection,
  className,
  onClick,
  ...props
}) => {

  const ctx = useCart();
  const { isPriceIdsFetching, isPriceError } = useProducts();

  const [incart, setIncart] = useState(() => ctx.cart.includes(collection));

  const handleAddToCart = createInCartHandler(collection, incart, setIncart, ctx);

  useEffect(() => {
    setIncart(ctx.cart.includes(collection));
  }, [ctx.cart, collection]);

  const isCartItem = collection.cartItem != undefined;
  const musioEssentials = collection?.authSkus.includes(MUS00003) || false;
  const collectionName = collection.collectionNameShort.length > 0 ? collection.collectionNameShort : collection.collectionName;

  return (
    <div
      className={cn(
        className,
        "bg-[#00000040] hover:bg-[#aaaaaa20] cursor-pointer p-3 rounded-xl",
        "flex flex-col space-y-2",
      )}
      onClick={onClick}
      {...props}
    >
      <div className="h-[200px] w-[200px]">
        <img
          className="w-full h-full object-cover"
          src={collection.assets.card_img?.url}
          alt={collection.collectionNameShort}
        />
      </div>

      {/*
           NOTE: the width is hard coded because I couldn't get these elements to inherit a set width from
          the top level parent of this component so I'm just matching the card so the elippsis will apply
      */}

      <div className="flex flex-col items-start w-full justify-between grow">
        <div className="flex flex-col space-y-0.5">
          <h2 className="font-bold text-musio-light-lilac truncate w-[200px]">{collectionName}</h2>
          <p className="text-musio-dark-lilac text-sm truncate w-[200px]">{collection.collectionSubtitle}</p>
        </div>

        <div className="">
          <div className="inline-flex items-center gap-1 min-w-0">
            {isPriceError && isCartItem ?
              <div className="flex items-center gap-2 text-musio-red italic pt-2">
                <AlertCircle />Error getting price
              </div>
              : isPriceIdsFetching && isCartItem ? (
                <div className="flex items-center gap-2 pt-2">
                  <div className="h-7 w-20 rounded bg-slate-700 animate-pulse" />
                </div>
              ) : (
                <>
                  {collection.cartItem?.salePriceActive && (
                    <span className="relative inline-block flex-shrink-0">
                      <p className="text-xl font-normal text-lilac whitespace-nowrap">
                        {collection.cartItem?.formattedPrice}
                      </p>
                      <span
                        className="absolute left-0 top-1/2 w-full h-0.5 bg-red-500 rotate-[-10deg] pointer-events-none"
                        style={{ transform: "rotate(-10deg)", top: "50%" }}
                      />
                    </span>
                  )}
                  <p
                    className={cn(
                      "text-[24px] font-normal whitespace-nowrap",
                      collection.cartItem?.salePriceActive ? "text-musio-green" : "text-lilac"
                    )}
                  >
                    {collection.cartItem?.salePriceActive ? collection.cartItem?.formattedSalePrice : collection.cartItem?.formattedPrice}
                  </p>
                </>
              )}
          </div>

          <div>
            {musioEssentials ?
              <p className="text-musio-green text-xl">Free</p>
              :
              isCartItem ? isPriceIdsFetching || isPriceError ? null :
                renderButtonByVariant["marketplace"]({
                  incart,
                  onClick: handleAddToCart,
                })
                : null}
          </div>
        </div>
      </div>
    </div>

  );

};

export default CollectionGrid;
